/**
 * A steady north (Sprint 103.1, ADR-0120 Rule 6).
 *
 * A phone has two ways to know where it points. The gyroscope measures each turn
 * smoothly and at once, but from wherever it started and with a slow drift. The
 * compass knows north, but each of its readings is noisy and pulled about by
 * metal nearby. So the gyroscope drives the view and the compass only says,
 * averaged over a couple of seconds, where north is in the gyroscope's frame:
 * one angle, the offset between the two, which barely changes while the phone
 * is used. That is what turns 180°, 200°, 170°, 190° into a steady 185°.
 */

/** The signed difference `a - b`, in (-180, 180]. */
export function angleDifference(a: number, b: number): number {
  const d = ((((a - b) % 360) + 540) % 360) - 180;
  return d === -180 ? 180 : d;
}

export interface NorthOptions {
  /** How slowly the compass moves north once settled, in seconds. */
  readonly timeConstantSeconds: number;
  /** How quickly it moves while settling, right after the first reading. */
  readonly settleTimeConstantSeconds: number;
  readonly settleSeconds: number;
  /** A reading this far from the estimate is set aside as a disturbance. */
  readonly outlierDegrees: number;
  /** Unless the disturbance lasts this long: then the estimate starts again from it. */
  readonly outlierResetSeconds: number;
}

export const NORTH_DEFAULTS: NorthOptions = {
  timeConstantSeconds: 2,
  settleTimeConstantSeconds: 0.25,
  settleSeconds: 1.5,
  outlierDegrees: 25,
  outlierResetSeconds: 3
};

/**
 * The offset between the gyroscope's frame and magnetic north, from a stream
 * of compass readings each expressed as "the offset this reading implies".
 */
export class NorthEstimate {
  private value: number | undefined;
  private startedAt = 0;
  private lastAt = 0;
  private outlierSince: number | undefined;
  /** A running mean of the squared difference between readings and the estimate. */
  private spread = 0;
  private readonly options: NorthOptions;

  constructor(options: Partial<NorthOptions> = {}) {
    this.options = { ...NORTH_DEFAULTS, ...options };
  }

  /** The offset in degrees, or undefined before the first reading. */
  get offset(): number | undefined {
    return this.value;
  }

  /** Whether the first readings have been averaged in. */
  settled(nowMs: number): boolean {
    return this.value !== undefined && nowMs - this.startedAt >= this.options.settleSeconds * 1000;
  }

  /** How far the compass readings scatter around the estimate, in degrees (one standard deviation). */
  get scatterDegrees(): number {
    return Math.sqrt(this.spread);
  }

  update(rawOffset: number, nowMs: number): void {
    const o = this.options;
    if (this.value === undefined) {
      this.restart(rawOffset, nowMs);
      return;
    }
    const dt = Math.max(0, Math.min(1, (nowMs - this.lastAt) / 1000));
    this.lastAt = nowMs;
    const difference = angleDifference(rawOffset, this.value);
    const settling = nowMs - this.startedAt < o.settleSeconds * 1000;
    if (!settling && Math.abs(difference) > o.outlierDegrees) {
      this.outlierSince ??= nowMs;
      // A disturbance that does not go away is the new field: a car parked, a
      // step away from a steel beam. Start again from it rather than stay wrong.
      if (nowMs - this.outlierSince >= o.outlierResetSeconds * 1000) this.restart(rawOffset, nowMs);
      return;
    }
    this.outlierSince = undefined;
    const tau = settling ? o.settleTimeConstantSeconds : o.timeConstantSeconds;
    const k = 1 - Math.exp(-dt / tau);
    this.value = (this.value + difference * k + 360) % 360;
    this.spread +=
      (difference * difference - this.spread) * (1 - Math.exp(-dt / o.timeConstantSeconds));
  }

  private restart(rawOffset: number, nowMs: number): void {
    this.value = (rawOffset + 360) % 360;
    this.startedAt = nowMs;
    this.lastAt = nowMs;
    this.outlierSince = undefined;
    this.spread = 0;
  }
}

/**
 * The share of the way towards a new reading a one-euro filter moves
 * (Casiez et al., 2012): heavy smoothing when the phone is still, light when it
 * turns fast, so the path neither trembles nor trails behind a turn.
 */
export function oneEuroAmount(
  dtSeconds: number,
  speedDegreesPerSecond: number,
  minCutoffHz = 0.8,
  beta = 0.04
): number {
  if (dtSeconds <= 0) return 1;
  const cutoff = minCutoffHz + beta * Math.abs(speedDegreesPerSecond);
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dtSeconds);
}

/**
 * A whole-degree readout that changes only when the value has moved by
 * `deadZone` degrees, so the number does not flicker between two values.
 */
export function steadyDegrees(shown: number | undefined, value: number, deadZone = 1): number {
  const rounded = ((Math.round(value) % 360) + 360) % 360;
  if (shown === undefined) return rounded;
  return Math.abs(angleDifference(value, shown)) >= deadZone ? rounded : shown;
}

export type CompassQuality = 'unknown' | 'good' | 'fair' | 'poor';

/** How a compass error, in degrees, reads to the person. */
export function compassQuality(errorDegrees: number | undefined): CompassQuality {
  if (errorDegrees === undefined || Number.isNaN(errorDegrees)) return 'unknown';
  if (errorDegrees <= 10) return 'good';
  if (errorDegrees <= 25) return 'fair';
  return 'poor';
}

/** The error shown on the chip, rounded up to 5° and never under 5°. */
export function shownError(errorDegrees: number): number {
  return Math.max(5, Math.ceil(errorDegrees / 5) * 5);
}
